/**
 * Cliente REST. Dono unico do access token e da renovacao dele.
 *
 * Responsabilidade: falar HTTP com a API, guardar o access token EM MEMORIA e
 * renova-lo antes de expirar.
 *
 * NAO e responsabilidade deste arquivo: guardar estado de aplicacao
 * (`store.ts`), desenhar (`views/`), nem manter o socket (`ws.ts` pede o token
 * aqui).
 *
 * ## Onde mora a credencial, e por que
 *
 * O access token (15 min) fica numa variavel de modulo e some quando a aba
 * fecha. Nao vai para localStorage: la ele sobrevive ao fechamento e qualquer
 * XSS o le. O que sustenta a sessao entre visitas e o cookie `gf_refresh` —
 * `httpOnly`, `path=/v1/auth`, rotativo com deteccao de reuso (C2). O JS desta
 * PWA nunca o enxerga; so o manda de volta ao chamar `/v1/auth/refresh`, que e
 * a unica rota sob aquele path que ele acompanha.
 *
 * Consequencia deliberada: recarregar a pagina custa um `POST /v1/auth/refresh`
 * antes de qualquer coisa aparecer. Sem rede, esse POST falha — e e ai que o
 * `cache.ts` entra, com dado velho e etiqueta de velho. Nunca com token velho.
 *
 * ## Um 401 nao e o fim da sessao
 *
 * O access token vale 15 min e a folga so cobre o relogio DESTE navegador;
 * celular que dormiu, aba estrangulada e desvio de relogio fazem o servidor
 * recusar um token que daqui ainda parecia bom. Enquanto o cookie de refresh
 * viver, isso e contratempo, nao sessao vencida: renova a forca e repete a
 * chamada uma vez. So a recusa do PROPRIO refresh manda o usuario para o
 * login. Antes disto, um unico 401 atravessado jogava fora uma sessao inteira
 * e pedia a senha de novo sem motivo.
 *
 * ## Uma renovacao de cada vez
 *
 * Tres chamadas paralelas com o token vencido renovariam tres vezes. Como o
 * refresh e ROTATIVO e reuso derruba a familia inteira (C2), a segunda
 * renovacao apresentaria um token ja marcado `used_at` e deslogaria o usuario
 * — um bug que so aparece sob concorrencia. Por isso `renovando` compartilha a
 * mesma promessa entre todos os chamadores.
 */

import { MAX_AMOSTRAS } from "./cache";
import type {
  ClaimCodeOut,
  CommandIn,
  CommandOut,
  KindUpload,
  PaginaAlertas,
  PrefsNotificacao,
  ReleaseOut,
  TelemetrySample,
  TokenOut,
  UploadOut,
  UserOut,
} from "./tipos";

/** Renova com esta folga antes do vencimento. */
const FOLGA_MS = 60_000;

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly detalhe: string,
  ) {
    super(detalhe);
    this.name = "ErroApi";
  }
}

/** Nao ha sessao: quem receber isto manda o usuario para o login. */
export class SemSessao extends Error {
  constructor(motivo = "sessão expirada") {
    super(motivo);
    this.name = "SemSessao";
  }
}

/** A rede nao respondeu. Diferente de 4xx/5xx: o servidor nem foi ouvido. */
export class SemRede extends Error {
  constructor(motivo = "sem rede") {
    super(motivo);
    this.name = "SemRede";
  }
}

let token: string | null = null;
let expiraEm = 0;
let renovando: Promise<string> | null = null;

function guardar(t: TokenOut): string {
  token = t.access_token;
  expiraEm = Date.now() + t.expires_in * 1000;
  return token;
}

export function esquecerToken(): void {
  token = null;
  expiraEm = 0;
  renovando = null;
}

/**
 * Quando o access token atual vence, em ms de epoca. `0` = nao ha token.
 *
 * Existe para o `ws.ts` reagendar a renovacao do socket depois de cada
 * renovacao: o `hello` do relay so anuncia a validade do PRIMEIRO token, e um
 * painel aberto a tarde inteira precisa saber a do token de agora.
 */
export const validadeDoToken = (): number => (token === null ? 0 : expiraEm);

/**
 * Devolve um access token utilizavel, renovando se preciso.
 *
 * `forcar` existe para o caso do relay levar 4401: o servidor ja provou que
 * nao aceita o token atual, entao esperar a folga seria insistir no que ja
 * falhou.
 */
export async function garantirToken(forcar = false): Promise<string> {
  if (!forcar && token !== null && Date.now() < expiraEm - FOLGA_MS) return token;
  if (renovando === null) {
    renovando = (async () => {
      const t = await pedir<TokenOut>("POST", "/v1/auth/refresh", undefined, false);
      return guardar(t);
    })().finally(() => {
      renovando = null;
    });
  }
  return renovando;
}

async function pedir<T>(
  metodo: string,
  caminho: string,
  corpo?: unknown,
  autenticado = true,
  repetindo = false,
): Promise<T> {
  const cabecalhos: Record<string, string> = {};
  if (corpo !== undefined) cabecalhos["Content-Type"] = "application/json";
  if (autenticado) cabecalhos["Authorization"] = `Bearer ${await garantirToken()}`;

  let resposta: Response;
  try {
    resposta = await fetch(caminho, {
      method: metodo,
      headers: cabecalhos,
      body: corpo === undefined ? null : JSON.stringify(corpo),
      // Mesma origem, mas explicito: e o cookie de refresh que sustenta a
      // sessao e um `omit` acidental deslogaria a cada carga.
      credentials: "same-origin",
      cache: "no-store",
    });
  } catch (erro) {
    throw new SemRede(erro instanceof Error ? erro.message : "falha de rede");
  }

  if (resposta.status === 401) {
    // Um 401 chega ANTES do handler: a dependencia de auth recusou e nada
    // aconteceu do outro lado, entao repetir e seguro ate para um POST.
    if (autenticado && !repetindo) {
      // Um `SemSessao` daqui e do refresh, e ai a sessao acabou de verdade.
      await garantirToken(true);
      return pedir<T>(metodo, caminho, corpo, autenticado, true);
    }
    esquecerToken();
    throw new SemSessao(await detalhe(resposta));
  }
  if (!resposta.ok) throw new ErroApi(resposta.status, await detalhe(resposta));
  if (resposta.status === 204) return undefined as T;
  return (await resposta.json()) as T;
}

async function detalhe(resposta: Response): Promise<string> {
  try {
    const corpo = (await resposta.json()) as { detail?: unknown };
    if (typeof corpo.detail === "string") return corpo.detail;
    // 422 do FastAPI: lista de erros de validacao. A primeira mensagem e a
    // que interessa — a gramatica de comando ja explica o problema em texto.
    if (Array.isArray(corpo.detail)) {
      const primeiro = corpo.detail[0] as { msg?: string } | undefined;
      if (primeiro?.msg !== undefined) return primeiro.msg.replace(/^Value error, /, "");
    }
  } catch {
    /* corpo vazio ou nao-JSON */
  }
  return `HTTP ${resposta.status}`;
}

// --- sessao ------------------------------------------------------------

export async function login(email: string, senha: string): Promise<void> {
  guardar(
    await pedir<TokenOut>("POST", "/v1/auth/login", { email, password: senha }, false),
  );
}

export async function registrar(
  email: string,
  senha: string,
  nomeDoTenant: string,
): Promise<void> {
  guardar(
    await pedir<TokenOut>(
      "POST",
      "/v1/auth/register",
      { email, password: senha, tenant_name: nomeDoTenant },
      false,
    ),
  );
}

export async function sair(): Promise<void> {
  try {
    await pedir<void>("POST", "/v1/auth/logout", undefined, false);
  } finally {
    esquecerToken();
  }
}

export const eu = (): Promise<UserOut> => pedir<UserOut>("GET", "/v1/auth/me");

// --- aparelhos ---------------------------------------------------------

// `GET /v1/devices` nao aparece aqui de proposito: a lista de aparelhos chega
// pelo SNAPSHOT do relay, ja com o digital twin de cada um junto (C8). Ter as
// duas fontes faria a tela nascer de um jeito quando o socket abre rapido e de
// outro quando demora.

export const criarClaimCode = (): Promise<ClaimCodeOut> =>
  pedir<ClaimCodeOut>("POST", "/v1/devices/claim-code");

/**
 * Publica um comando. `201` significa "publicado no broker em QoS1", nao
 * "executado" — quem fecha a linha e o ack, que chega pelo relay depois.
 */
export const enviarComando = (deviceId: string, corpo: CommandIn): Promise<CommandOut> =>
  pedir<CommandOut>("POST", `/v1/devices/${deviceId}/commands`, corpo);

export const comandos = (deviceId: string, limite = 20): Promise<CommandOut[]> =>
  pedir<CommandOut[]>("GET", `/v1/devices/${deviceId}/commands?limit=${limite}`);

/**
 * O que ESTE aparelho pode instalar, mais nova primeiro.
 *
 * A lista ja vem filtrada pelo servidor — board certo, sem release retirada,
 * sem downgrade, sem `min_from` fora de alcance — e a tela nao refiltra: e o
 * MESMO filtro que o `firmware/grant` aplica depois
 * (`app/services/firmware.py::motivo_de_recusa`), entao o botao nunca oferece
 * o que o grant vai recusar.
 *
 * Lista vazia e resposta normal e comum: "este aparelho esta na versao mais
 * nova que existe para a placa dele".
 *
 * Nao ha `POST` equivalente aqui, e nem poderia haver: publicar release e
 * `api/scripts/publish_firmware.py`, no container, e a tabela tem RLS com
 * politica so de SELECT.
 */
export const firmware = (deviceId: string): Promise<ReleaseOut[]> =>
  pedir<ReleaseOut[]>("GET", `/v1/devices/${deviceId}/firmware`);

/**
 * Imagens da janela pedida, mais recente primeiro.
 *
 * `desde`/`ate` sao ISO e viram `since`/`until` na rota — sem eles a janela e
 * aberta e voltam os `LIMITE_FRAMES` mais recentes. E o seletor de periodo do
 * timelapse que os preenche; o teto espelha o `le=1000` do endpoint, que sao
 * ~41 dias na cadencia horaria do `gfc`.
 *
 * `kind` e sempre passado pelos chamadores, nunca omitido: as duas telas
 * querem coisas diferentes, e a rota sem filtro devolve os dois tipos
 * misturados — um snapshot no meio do scrub saltaria para uma foto avulsa no
 * meio do filme.
 */
export const LIMITE_FRAMES = 1000;

/**
 * Quantos snapshots a tela guarda. Espelha o `MAX_SNAPSHOTS` do
 * `app/services/uploads.py`: pedir mais do que a nuvem guarda so gastaria
 * resposta.
 */
export const LIMITE_SNAPSHOTS = 10;

export interface JanelaUploads {
  desde?: string;
  ate?: string;
}

export const uploads = (
  deviceId: string,
  janela: JanelaUploads = {},
  kind: KindUpload = "timelapse",
): Promise<UploadOut[]> => {
  const teto = kind === "snapshot" ? LIMITE_SNAPSHOTS : LIMITE_FRAMES;
  const params = new URLSearchParams({ limit: String(teto), kind });
  if (janela.desde !== undefined) params.set("since", janela.desde);
  if (janela.ate !== undefined) params.set("until", janela.ate);
  return pedir<UploadOut[]>("GET", `/v1/devices/${deviceId}/uploads?${params}`);
};

/**
 * Historico de telemetria, mais antigo primeiro — a serie que o relay NAO tem.
 *
 * O socket so entrega o que acontecer com a aba ja aberta, entao ate a C9 uma
 * aba recem-carregada nascia com a sparkline vazia e um numero solto vindo do
 * digital twin. O limite espelha `MAX_AMOSTRAS` do `cache.ts`: pedir mais do
 * que o buffer da tela guarda seria baixar dado para joga-lo fora.
 */
export const telemetria = (
  deviceId: string,
  limite = MAX_AMOSTRAS,
): Promise<TelemetrySample[]> =>
  pedir<TelemetrySample[]>("GET", `/v1/devices/${deviceId}/telemetry?limit=${limite}`);

/**
 * A janela pedida para o grafico de periodo escolhivel — a mesma rota,
 * `since`/`until`/`bucket` no lugar do `limit` fixo da sparkline.
 *
 * Sem `since`/`until` o servidor ja responde as ultimas 24 h
 * (`app/routers/devices.py::listar_telemetria`). `bucket`, quando presente,
 * pede agregacao no servidor (min/max por balde) — quem decide QUANDO pedir
 * balde e `acoes.ts`, este arquivo so encaixa o parametro na URL, do mesmo
 * jeito que `uploads()` encaixa `since`/`until`.
 */
export interface JanelaSerie {
  desde?: string;
  ate?: string;
  bucket?: number;
}

export const serie = (deviceId: string, janela: JanelaSerie): Promise<TelemetrySample[]> => {
  const params = new URLSearchParams({ limit: String(janela.bucket === undefined ? 1440 : LIMITE_BALDES) });
  if (janela.desde !== undefined) params.set("since", janela.desde);
  if (janela.ate !== undefined) params.set("until", janela.ate);
  if (janela.bucket !== undefined) params.set("bucket", String(janela.bucket));
  return pedir<TelemetrySample[]>("GET", `/v1/devices/${deviceId}/telemetry?${params}`);
};

/**
 * Teto de baldes pedido quando `bucket` esta presente. Espelha `BALDES` de
 * `acoes.ts`: o servidor devolve ate 2 linhas por balde, entao isto e o que
 * mantem a resposta longe do teto de 5000 do endpoint mesmo na janela mais
 * larga (30 d).
 */
const LIMITE_BALDES = 700;

// --- alertas e notificacoes (C12) --------------------------------------

/**
 * O feed, mais novo primeiro, com o contador de nao-lidos junto.
 *
 * Vem por REST e nao pelo snapshot do relay, no mesmo padrao de `comandos()` e
 * `uploads()`: alargar o snapshot faria todo socket que reconecta pagar o
 * historico de novo, inclusive os que reconectam so porque o token venceu.
 * O que chega pelo socket e o alerta NOVO, um a um.
 *
 * `deviceId` filtra a lista, mas nao o contador: o badge mora no trilho e fala
 * do tenant inteiro — inclusive de um aparelho que nao esta na tela.
 */
export const alertas = (
  deviceId?: string,
  limite = LIMITE_ALERTAS,
): Promise<PaginaAlertas> => {
  const params = new URLSearchParams({ limite: String(limite) });
  if (deviceId !== undefined) params.set("device_id", deviceId);
  return pedir<PaginaAlertas>("GET", `/v1/alerts?${params}`);
};

/** Espelha `MAX_ALERTAS` do `cache.ts`: pedir mais do que a tela guarda seria
 * baixar dado para jogar fora. */
export const LIMITE_ALERTAS = 50;

/**
 * Marca como lido ate `ate` (ISO), ou tudo quando omitido.
 *
 * O `ate` existe para a tela nao apagar o badge de um alerta que chegou DEPOIS
 * de ela ter desenhado a lista: quem marca diz ate onde leu, e nao "agora".
 */
export const marcarAlertasLidos = (ate?: string): Promise<PaginaAlertas> =>
  pedir<PaginaAlertas>("POST", "/v1/alerts/read", ate === undefined ? {} : { ate });

/**
 * A chave publica VAPID. String vazia = o servidor nao tem push configurado.
 *
 * A tela precisa disto ANTES de pedir permissao ao usuario: pedir e depois
 * falhar gasta a unica chance que o navegador da — negada uma vez, so nas
 * configuracoes do site da para voltar atras.
 */
export const chavePush = (): Promise<{ chave: string }> =>
  pedir<{ chave: string }>("GET", "/v1/push/key");

/** O objeto e o mesmo que `PushSubscription.toJSON()` devolve, sem adaptador. */
export interface InscricaoPush {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

export const inscreverPush = (inscricao: InscricaoPush): Promise<void> =>
  pedir<void>("POST", "/v1/push/subscriptions", inscricao);

export const desinscreverPush = (inscricao: InscricaoPush): Promise<void> =>
  pedir<void>("DELETE", "/v1/push/subscriptions", inscricao);

export const prefsNotificacao = (): Promise<PrefsNotificacao> =>
  pedir<PrefsNotificacao>("GET", "/v1/push/prefs");

/** Substituicao completa, nao patch: sao quatro booleanos numa tela so. */
export const gravarPrefsNotificacao = (
  prefs: PrefsNotificacao,
): Promise<PrefsNotificacao> => pedir<PrefsNotificacao>("PUT", "/v1/push/prefs", prefs);
